import { Box, SidePanel } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useRef, useState } from 'react';

import { RicosContext } from './../../../ricos-context';
import { HTMLData_Source } from './../../../ricos-schema/legacy/wix/rich_content/v1';
import { useBiNext } from './../../../_shared/bi-context-provider/bi-context-provider';
import { containsUrl } from './../../../_shared/link/contains-url';
import type { PmHtmlNode } from './../../../_shared/pm-node-types';
import { useNodeAttrs } from './../../../_shared/use-node-attrs';
import { HTML_EDIT_MODAL_ID } from './../../consts';
import { FooterActions, Panel, PanelHeader } from './../../../ui-components';

import { EDIT_PANEL, EDIT_PANEL_FOOTER_ACTIONS } from '../../data-hooks';
import type { AdsenseSourceInputProps } from './AdsenseSourceInput';
import { AdsenseSourceInput } from './AdsenseSourceInput';
import type { HtmlSourceInputProps } from './HtmlSourceInput';
import { HtmlSourceInput } from './HtmlSourceInput';

export type InsertModalProps = {
  nodeId: string;
  closeModal: () => void;
};

export const InsertModal: FC<InsertModalProps> = ({ nodeId, closeModal }) => {
  const { t } = useContext(RicosContext);
  const logBi = useBiNext({ pluginId: 'html', modalId: HTML_EDIT_MODAL_ID, nodeId });
  const { patchPmNodeAttrs, pmNodeAttrs } = useNodeAttrs(nodeId, 'HTML');

  const [htmlNodeAttrs, setHtmlNodeAttrs] = useState<Partial<PmHtmlNode['attrs']> | undefined>(
    pmNodeAttrs
  );

  const inputRef = useRef<HTMLInputElement | null>(null);

  const isAdsense = htmlNodeAttrs?.isAdsense ?? htmlNodeAttrs?.source === HTMLData_Source.ADSENSE;

  const HtmlComponent: React.FC<AdsenseSourceInputProps | HtmlSourceInputProps> = isAdsense
    ? AdsenseSourceInput
    : HtmlSourceInput;

  return (
    <Panel onCloseButtonClick={closeModal} dataHook={EDIT_PANEL}>
      <PanelHeader title={t('HtmlPlugin_Edit_Modal_Title')} />
      <Box direction="vertical" padding="SP3">
        <HtmlComponent
          htmlNodeAttrs={htmlNodeAttrs}
          onHtmlInputChange={(html: string) => {
            setHtmlNodeAttrs({ url: null, html });
          }}
          onLinkInputChange={(url: string) => {
            setHtmlNodeAttrs({ url: url ?? null, html: null });
          }}
          inputRef={inputRef}
        />
      </Box>
      <SidePanel.Footer showDivider>
        <FooterActions
          onCancel={closeModal}
          onSave={() => {
            if (htmlNodeAttrs?.url && !containsUrl(htmlNodeAttrs.url)) {
              inputRef.current?.focus();
              return;
            }

            htmlNodeAttrs && patchPmNodeAttrs?.(htmlNodeAttrs);

            closeModal();
          }}
          saveButtonLabel={t('HtmlEditPanel_Update')}
          dataHook={EDIT_PANEL_FOOTER_ACTIONS}
          logBi={logBi}
        />
      </SidePanel.Footer>
    </Panel>
  );
};
